import React, { useCallback, useEffect, useState } from "react";

import * as yup from "yup";
import { EventType } from "@azure/msal-browser";
import { useMsal } from "@azure/msal-react";
import { Button, Stack, TextField, Typography } from "@mui/material";
import { msalConfig } from "authConfig";
import { AsyncContentMui, ValidationError } from "components";
import { FileViewer } from "components/popups";
import { useSSOContext } from "components/providers/SSO-provider/SSOContext";
import { useFormik } from "formik";
import { useActionHook, useDidUpdate } from "hooks";
import { IApiServiceError, useAuthActions } from "store";
import { ApiValidationErrors, IAuthActions, IAzureClient, IFile } from "store/types";

import { FileType } from "../../constants";
import { AzureLogin } from "./AzureLogin";
import { JumpCloud } from "./JumpCloud";

type InitState = {
  email: string;
  password?: string;
};

const schema = yup.object().shape({
  email: yup.string().email("The email must be a valid email address.").required(),
  password: yup.string().required()
});

interface DefaultChoiceProps {
  goToForgetPass: () => void;
  setEmail: (value: string) => void;
  onEasyLogin: IAuthActions["easyLoginAction"];
  inProgress: boolean;
  onSubmit: (valules: InitState) => void;
  apiValidationErrors?: { errors: ApiValidationErrors };
  resetApiErrors: () => void;
  getPrivacyPolicy: () => Promise<Pick<IFile, "file_url">>;
  getTerms: () => Promise<Pick<IFile, "file_url">>;
}

export const DefaultChoice: React.FC<DefaultChoiceProps> = ({
  goToForgetPass,
  setEmail,
  onEasyLogin,
  inProgress,
  onSubmit,
  apiValidationErrors,
  resetApiErrors,
  getPrivacyPolicy,
  getTerms
}): JSX.Element => {
  const { loginWithAzureAction, sessionAction } = useAuthActions();
  const [error, setError] = useState<string>("");
  const { accounts, instance } = useMsal();
  const [pdf, setPdf] = useState<Pick<IFile, "file_url" | "type"> | null>(null);

  const { azure_sso_enabled, saml_sso_enabled, sso_only } = useSSOContext();

  const onClose = () => {
    setPdf(null);
  };

  const initialState = {
    email: "",
    password: ""
  };

  const { values, handleChange, errors, handleSubmit, isValid } = useFormik({
    initialValues: initialState,
    enableReinitialize: true,
    validationSchema: schema,
    onSubmit: onSubmit
  });

  const onEasy = useCallback(async () => {
    try {
      await schema.shape({ password: yup.string().notRequired() }).validate({ email: values.email });
      await onEasyLogin(values.email);
    } catch (error) {
      const { errors } = error as IApiServiceError;
      if (errors?.email?.[0]) {
        setError(errors?.email?.[0]);
      } else {
        setError("Something went wrong");
      }
    }
  }, [values.email]);

  const {
    action: loginWithAzure,
    error: withAzureErrors,
    loading: withAzureLoading,
    setLoading: setAzureLoading,
    resetError: resetWithAzureError
  } = useActionHook(loginWithAzureAction, {
    onSuccess: async () => {
      await sessionAction([]);
    }
  });

  const azureLogout = useCallback(async () => {
    const accountId = accounts[0]?.homeAccountId;
    if (accountId) {
      const currentAccount = instance.getAccountByHomeId(accountId);
      await instance.logoutRedirect({
        account: currentAccount,
        postLogoutRedirectUri: msalConfig.auth.redirectUri
      });
      resetWithAzureError();
    }
  }, [accounts, instance]);

  useDidUpdate(() => {
    if (apiValidationErrors?.errors?.login) {
      resetApiErrors();
    }
  }, [values]);

  const showPrivacy = useCallback(async () => {
    const { file_url } = await getPrivacyPolicy();
    if (file_url) {
      setPdf({
        file_url: file_url,
        type: FileType.PDF
      });
    }
  }, []);

  const showTerms = useCallback(async () => {
    const { file_url } = await getTerms();
    if (file_url) {
      setPdf({
        file_url: file_url,
        type: FileType.PDF
      });
    }
  }, []);

  useEffect(() => {
    if (!(instance as IAzureClient).initialized) {
      return;
    }
    const callbackId = instance.addEventCallback((event: any) => {
      if (event.eventType === EventType.LOGIN_SUCCESS || event.eventType === EventType.ACQUIRE_TOKEN_SUCCESS) {
        loginWithAzure(event.payload.accessToken);
      }
    });
    return () => {
      instance.removeEventCallback(callbackId as string);
    };
  }, []);

  return (
    <Stack sx={{ alignItems: "stretch", gap: "50px", maxWidth: "300px" }}>
      <Typography
        sx={{
          color: "#173c4c",
          fontFamily: "Oswald, sans-serif",
          fontSize: "2.5rem",
          fontWeight: 300,
          letterSpacing: "1.25px",
          lineHeight: "60px"
        }}
      >
        Login
      </Typography>
      {apiValidationErrors?.errors?.login && <ValidationError messages={apiValidationErrors.errors.login} />}
      <AsyncContentMui isLoading={inProgress || withAzureLoading} spinnerProps={{ size: 150 }}>
        <Stack gap="20px">
          {sso_only && (
            <Typography sx={{ textAlign: "center", fontSize: "0.9rem", color: "#173c4c" }}>
              This account requires SSO login.
            </Typography>
          )}
          {!sso_only && (
            <>
              <form
                onSubmit={handleSubmit}
                style={{ display: "flex", flexDirection: "column", gap: "20px", maxWidth: "300px" }}
              >
                <TextField
                  placeholder="Enter email"
                  label="Email"
                  value={values.email}
                  error={!!errors.email || !!error}
                  helperText={errors.email || error}
                  name="email"
                  onChange={(e) => {
                    handleChange(e);
                    setEmail(e.target.value);
                    setError("");
                  }}
                />
                <TextField
                  placeholder="Enter password"
                  label="Password"
                  value={values.password}
                  error={!!errors.password}
                  helperText={errors.password}
                  type="password"
                  name="password"
                  onChange={handleChange}
                />
                <Button
                  disabled={!isValid}
                  variant="contained"
                  sx={{
                    borderRadius: "5px",
                    fontSize: "18px",
                    textTransform: "none"
                  }}
                  type="submit"
                >
                  Login
                </Button>
              </form>
              <Typography
                className="scale-click"
                onClick={goToForgetPass}
                sx={{ fontSize: "0.8rem", textAlign: "center", color: "#173c4c", cursor: "pointer" }}
              >
                Forgot your password?
              </Typography>
              <Button disabled={!!error} onClick={onEasy} variant="outlined" sx={{ borderRadius: "5px" }}>
                Easy Login
              </Button>
              <Typography sx={{ textAlign: "center", fontSize: "0.8rem", color: "#173c4c" }}>
                * You will receive an email with a login code. enter it in the following screen to enter the
                platform
              </Typography>
            </>
          )}
          {azure_sso_enabled && <AzureLogin setLoading={setAzureLoading} />}
          {saml_sso_enabled && <JumpCloud />}
          <Stack
            direction="row"
            sx={{
              ".MuiTypography-root": { fontSize: "0.8rem", cursor: "pointer", textDecoration: "underline" },
              fontSize: "0.8rem",
              color: "#173c4c",
              gap: "3px",
              alignItems: "center",
              justifyContent: "center"
            }}
          >
            <Typography onClick={showTerms}>Terms of Service</Typography>&
            <Typography onClick={showPrivacy}>Privacy Policy</Typography>
            {pdf?.file_url && <FileViewer file={pdf} isOpen={!!pdf} onClose={onClose} />}
          </Stack>
          {withAzureErrors?.errors["login"] && <ValidationError messages={withAzureErrors.errors["login"]} />}
          {withAzureErrors?.errors["login"] && (
            <Button onClick={azureLogout} variant="outlined">
              Logout From Azure
            </Button>
          )}
        </Stack>
      </AsyncContentMui>
    </Stack>
  );
};
